import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Slide-puzzle captcha.
 *
 * The server cuts a piece out of one of the site's own photos and keeps the correct position; the
 * visitor drags the piece into the gap and we send back where they dropped it. On success the
 * parent gets the token + x to submit with the form (single use, expires in 10 minutes).
 *
 * Two things this component is careful about:
 *  - a NEW challenge is only fetched on mount or when "New puzzle" is pressed. The callbacks are
 *    held in refs so a parent re-render can never reload the puzzle and throw away the visitor's
 *    progress.
 *  - a wrong answer keeps the position, so the visitor (or the keyboard) can nudge and retry.
 *
 * Mouse, touch and keyboard (arrow keys + Enter) all work.
 */
const SlidePuzzleCaptcha = ({ onSolved, onReset }) => {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [x, setX] = useState(0);
  const [state, setState] = useState('idle');   // idle | checking | solved | failed
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const xRef = useRef(0);
  const onSolvedRef = useRef(onSolved);
  const onResetRef = useRef(onReset);

  useEffect(() => { onSolvedRef.current = onSolved; onResetRef.current = onReset; }, [onSolved, onReset]);
  useEffect(() => { xRef.current = x; }, [x]);

  const load = useCallback(async () => {
    setLoading(true);
    setState('idle');
    setX(0);
    xRef.current = 0;
    onResetRef.current?.();
    try {
      const res = await axios.get(`${API_URL}/api/captcha/slide`);
      setChallenge(res.data);
    } catch (error) {
      setChallenge(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const maxX = challenge
    ? (challenge.max_x ?? challenge.width - challenge.piece_size - Math.round(challenge.piece_size / 4))
    : 0;

  /** Verify the given position. A wrong answer keeps the position so it can be nudged and retried. */
  const check = useCallback(async (position, token) => {
    if (!token) return;
    setState('checking');
    try {
      await axios.post(`${API_URL}/api/captcha/slide/verify`, { token, x: position });
      setState('solved');
      onSolvedRef.current?.({ token, x: position });
    } catch (error) {
      setState('failed');
      onResetRef.current?.();
    }
  }, []);

  const positionFromEvent = useCallback((event) => {
    const track = trackRef.current;
    if (!track || !challenge) return 0;
    const rect = track.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const scale = challenge.width / rect.width;          // the board is responsive
    const raw = (clientX - rect.left) * scale - challenge.piece_size / 2;
    return Math.max(0, Math.min(maxX, Math.round(raw)));
  }, [challenge, maxX]);

  const startDrag = (event) => {
    if (state === 'solved' || !challenge) return;
    draggingRef.current = true;
    const next = positionFromEvent(event);
    setX(next);
    xRef.current = next;
    setState('idle');
  };

  // one set of window listeners for the life of the component
  useEffect(() => {
    const move = (event) => {
      if (!draggingRef.current) return;
      const next = positionFromEvent(event);
      setX(next);
      xRef.current = next;
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      check(xRef.current, challenge?.token);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, [positionFromEvent, check, challenge]);

  const onKeyDown = (event) => {
    if (!challenge || state === 'solved') return;
    const step = event.shiftKey ? 16 : 4;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      setX((prev) => {
        const next = Math.max(0, Math.min(maxX, prev + (event.key === 'ArrowRight' ? step : -step)));
        xRef.current = next;
        return next;
      });
      setState('idle');
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      check(xRef.current, challenge.token);
    }
  };

  const solved = state === 'solved';

  return (
    <div data-testid="slide-captcha">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">
          {solved ? 'Verified - thank you' : `${challenge?.instruction || 'Drag the piece into the gap'} *`}
        </span>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-700"
          data-testid="slide-captcha-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" /> New puzzle
        </button>
      </div>

      <div
        className={`relative overflow-hidden rounded-lg border-2 bg-slate-100 ${
          solved ? 'border-emerald-500' : state === 'failed' ? 'border-red-400' : 'border-slate-300'
        }`}
        style={{ aspectRatio: challenge ? `${challenge.width} / ${challenge.height}` : '16 / 9' }}
        data-testid="slide-captcha-board"
      >
        {loading || !challenge ? (
          <div className="flex h-full items-center justify-center text-slate-400">
            {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <span className="text-sm">Puzzle unavailable - press New puzzle</span>}
          </div>
        ) : (
          <>
            <img src={challenge.background} alt="" className="pointer-events-none absolute inset-0 h-full w-full select-none" />
            <img
              src={challenge.piece}
              alt="puzzle piece"
              draggable={false}
              onMouseDown={startDrag}
              onTouchStart={startDrag}
              className={`absolute select-none drop-shadow-lg ${solved ? '' : 'cursor-grab active:cursor-grabbing'}`}
              style={{
                left: `${(x / challenge.width) * 100}%`,
                top: `${(challenge.piece_y / challenge.height) * 100}%`,
                width: `${((challenge.piece_size + challenge.piece_size / 4) / challenge.width) * 100}%`,
                touchAction: 'none',
              }}
              data-testid="slide-captcha-piece"
            />
            {solved && (
              <span className="absolute inset-0 flex items-center justify-center bg-emerald-500/20" data-testid="slide-captcha-solved">
                <span className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-bold text-emerald-700">
                  <Check className="h-4 w-4" /> Verified
                </span>
              </span>
            )}
          </>
        )}
      </div>

      {/* slider: drag it or use the arrow keys, then Enter */}
      <div
        ref={trackRef}
        className="relative mt-2 h-10 rounded-full bg-slate-200 outline-none focus:ring-2 focus:ring-blue-500"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onKeyDown={onKeyDown}
        role="slider"
        tabIndex={0}
        aria-label="Slide the puzzle piece into the gap"
        aria-valuemin={0}
        aria-valuemax={maxX}
        aria-valuenow={x}
        data-x={x}
        data-max-x={maxX}
        data-state={state}
        data-testid="slide-captcha-track"
      >
        <div
          className={`pointer-events-none absolute top-0 flex h-10 items-center justify-center rounded-full px-4 text-white shadow ${
            solved ? 'bg-emerald-600' : 'bg-blue-800'
          }`}
          style={{ left: `${maxX ? (x / maxX) * 88 : 0}%`, touchAction: 'none' }}
          data-testid="slide-captcha-handle"
        >
          {state === 'checking' ? <Loader2 className="h-4 w-4 animate-spin" /> : solved ? <Check className="h-4 w-4" /> : '\u2192'}
        </div>
      </div>

      {state === 'failed' && (
        <p className="mt-1.5 text-sm text-red-600" data-testid="slide-captcha-error">
          Not quite in the gap - nudge it and try again, or press New puzzle.
        </p>
      )}
      <p className="mt-1 text-[11px] text-slate-400">
        Prefer the keyboard? Focus the slider, use the arrow keys, then press Enter.
      </p>
    </div>
  );
};

export default SlidePuzzleCaptcha;
